import { useMemo } from "react";
import { useLineDraftState } from "@/features/lines/draft/useLineDraftState";
import { useLineCanvas } from "@/features/lines/canvas/hooks";
import { useLineMachines } from "@/features/lines/hooks";
import { useLineDetail } from "@/features/line-editor/hooks";
import type { LineCanvas } from "@/types/canvas.types";
import type { MachineDetail } from "@/types/lines.types";
import type { ValidationIssue } from "@/types/line-editor.types";
import { computeCreationIssues } from "./computeCreationIssues";
import { computeEditIssues } from "./computeEditIssues";

interface Params {
  mode: "creation" | "edit";
  lineId?: string;
  /**
   * O canvas COMO ESTÁ SENDO EDITADO, publicado pelo `ProductionLineCanvas`.
   *
   * Quando presente, SUBSTITUI o canvas do servidor no cálculo das pendências. É o que faz o painel
   * responder a arrastar uma máquina para o canvas, em vez de continuar pedindo — com a máquina
   * visivelmente lá — que ela seja arrastada.
   *
   * `null`/ausente = nada foi publicado ainda ⇒ vale o canvas salvo. É o estado do primeiro render,
   * antes de o canvas montar.
   */
  editedCanvas?: LineCanvas | null;
}

/**
 * O array vazio ESTÁVEL para quando a query ainda não tem dados.
 *
 * ⚠️ Um `= []` no destructuring parece a mesma coisa e não é: ele constrói um array NOVO a cada
 * render, e a identidade dele é dependência do `useMemo` abaixo. Enquanto `useLineMachines` está
 * sem dados — carregando, ou 401 com a sessão expirada — o memo era invalidado em toda passagem e
 * devolvia um `ValidationIssue[]` novo a cada render.
 *
 * A ponta solta ficava em `useStableIssueState`, cujo efeito depende dessa identidade: render →
 * efeito → `setState` → render → efeito… O React aborta em 50 updates encadeados com "Maximum
 * update depth exceeded" e DESCARTA as atualizações daquele ciclo. Na tela isso apareceu como a
 * máquina arrastada para o canvas que não era registrada — um sintoma que não menciona validação,
 * num arquivo que não menciona canvas.
 */
const NO_MACHINES: MachineDetail[] = [];

export function useUnifiedDraftIssues({ mode, lineId, editedCanvas }: Params): ValidationIssue[] {
  const isEdit = mode === "edit";
  const safeLineId = isEdit ? (lineId ?? "") : "";

  const { draft } = useLineDraftState();
  const { data: line } = useLineDetail(safeLineId);
  const { data: machinesData } = useLineMachines(safeLineId, isEdit ? 30_000 : undefined);
  const { data: canvas } = useLineCanvas(safeLineId, isEdit);

  // `line` e `canvas` já são estáveis quando ausentes (`undefined` é `undefined`); só as máquinas
  // precisavam do cuidado, porque só elas tinham valor padrão.
  const machines = machinesData ?? NO_MACHINES;

  // O canvas EM EDIÇÃO vence o salvo. O do servidor continua sendo o ponto de partida: vale
  // enquanto o editor não publicou nada, e é dele que o próprio editor foi semeado — então no
  // primeiro instante os dois descrevem a mesma coisa e a troca não pisca nada na tela.
  const effectiveCanvas = editedCanvas ?? canvas;

  return useMemo<ValidationIssue[]>(() => {
    if (mode === "creation") return computeCreationIssues(draft);
    if (!line || !effectiveCanvas) return [];
    return computeEditIssues({ line, machines, canvas: effectiveCanvas });
  }, [mode, draft, line, machines, effectiveCanvas]);
}
